import { NodeLayout } from '@/components/nodes/layout';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { OptionRemark } from '@/components/nodes/shared/option-remark';
import { TextEditDialog } from '@/components/nodes/text/edit-dialog';
import { EditorProvider } from '@/components/ui/kibo-ui/editor';
import { textToTiptapJSON } from '@/lib/utils';
import { download } from '@/lib/download';
import { handleError } from '@/lib/error/handle';
import { speechModels } from '@flowmuse/models/speech';
import { getDefaultModelId, getLastSelectedModelId, getPresetModelId, getPresetModelParams, rememberLastSelectedModelId } from '@flowmuse/models/shared/utils';
import { getUiOptionsForModel, getInitialModelUi, type UiOption } from '@flowmuse/models/speech/ui-options';
import { getSpeechVoiceLabel } from '@flowmuse/models/speech/voice-label';
import { getDescriptionsFromImageNodes, getTextFromTextNodes } from '@/lib/xyflow';
import { useProject } from '@/providers/project';
import { getIncomers, useNodeConnections, useReactFlow, useStore } from '@xyflow/react';
import { selectNodeSelectedById } from '@/components/nodes/store-selectors';
import { AudioLines as AudioIcon, Zap, Music, MicVocal, Dice5, Copy, Check, Share2 } from 'lucide-react';
import { AudioPlayer } from '@/components/ui/audio-player';
import { type ChangeEventHandler, type ComponentProps, type FormEventHandler, type ReactNode, memo, useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { mutate } from 'swr';
import type { AudioNodeProps } from '.';
import { Slider } from '@/components/ui/slider';
import { ThreeRowToolbar, InstructionInput, NodeSurface, ModelItem, GenerateItem, DownloadItem, SimpleSelectItem, type SimpleOption, ProcessingSurface, useLinearProgress, HistoryItem, AdvancedToggleButton, AdvancedInlinePanel, InlineOptionsRow, InlineMediaUploader, type InlineConnectedItem } from '@/components/nodes/shared';
import { classifyNodeMediaType, type InlineAttachment, type InlineMediaConfig } from '@flowmuse/models/inline-attachments';
import { createAudioHandleGenerate } from '@/components/nodes/audio/handle-generate';
import { cancelGeneration } from '@/lib/cancel-generation';
import { useAutoCancelHandler, useAutoGenerateHandler } from '@/components/canvas/utils/auto-generate-bus';
import { ModeChip } from '@/components/ui/mode-chip';
import { useI18n } from '@/providers/i18n';
import { useSubscription, filterModelsClient } from '@/providers/subscription';
import { useEstimatedBilling } from '@/hooks/use-estimated-credits';
import { estimateSpeechUsdCost } from '@/lib/cost/speech-estimate';
import { useAudioRealtime } from '@/components/nodes/audio/handle-realtime';
import { useAudioPolling } from '@/components/nodes/audio/handle-polling';
import { nanoid } from 'nanoid';
import { AudioCollapsedSummary } from '@/components/nodes/audio/collapsed-summary';
import { MultiAudioPicker } from '@/components/nodes/audio/multi-audio-picker';
import { MusicShareDialog } from '@/components/nodes/audio/music-share-dialog';
import { AudioTopToolbar } from '@/components/nodes/audio/audio-top-toolbar';
import { resolveAssetRefPreview } from '@flowmuse/models/shared/asset-ref';

type AudioTransformProps = AudioNodeProps & {
  title: string;
};

// 模块级稳定 icon 元素，保证 InstructionRow 的 memo 生效（避免每次渲染重建）。
const PROMPT_ROW_ICON = <Music size={12} className="text-muted-foreground/50" />;
const LYRICS_ROW_ICON = <MicVocal size={12} className="text-muted-foreground/50" />;

/** 文本/歌词输入行：标签 + 输入框。prompt 与 lyrics 复用同一结构。 */
const InstructionRow = memo(function InstructionRow({
  icon,
  label,
  textareaRef,
  value,
  onChange,
  onInput,
  placeholder,
}: {
  icon: ReactNode;
  label: string;
  textareaRef: ComponentProps<typeof InstructionInput>['textareaRef'];
  value: string;
  onChange: ChangeEventHandler<HTMLTextAreaElement>;
  onInput: FormEventHandler<HTMLTextAreaElement>;
  placeholder: string;
}) {
  return (
    <div className="flex items-center gap-1.5 px-2 py-1">
      <div className="flex items-center justify-center gap-1 shrink-0 w-[60px]">
        {icon}
        <span className="text-[10px] font-semibold text-muted-foreground/50 uppercase tracking-wider select-none">
          {label}
        </span>
      </div>
      <div className="relative flex-1 min-w-0">
        <InstructionInput
          textareaRef={textareaRef}
          value={value}
          onChange={onChange}
          onInput={onInput}
          showPlaceholder={!value.length}
          placeholder={placeholder}
        />
      </div>
    </div>
  );
});

export const AudioTransform = memo(({
  data,
  id,
  type,
  title,
}: AudioTransformProps) => {
  const { t } = useI18n();
  const { updateNodeData, getNodes, getEdges, getNode, setCenter, getZoom } = useReactFlow();
  const [loading, setLoading] = useState(false);
  const project = useProject();
  const modelId =
    data.model ??
    getPresetModelId('speech', speechModels) ??
    getLastSelectedModelId('speech', speechModels) ??
    getDefaultModelId(speechModels);
  const model = speechModels[modelId];
  // 真实可选音色（排除 'lyrics' 占位）。可能多达数百个，memo 化避免重复过滤。
  const displayVoices = useMemo(
    () => (model?.voices || []).filter((v: string) => v !== 'lyrics'),
    [model?.voices],
  );

  // 线性进度：优先使用服务端按真实完成记录计算的缓存百分位，未就绪时回退到默认值。
  const { progress, remainingText, hasEstimate } = useLinearProgress({
    isProcessing: (data as any)?.status === 'processing',
    updatedAt: (data as any)?.updatedAt,
    runtimeEstimatedTime: Number((data as any)?.runtimeEstimatedTime),
    fallbackSeconds: 60,
  });

  // 回填：节点初次创建且未设置 model 时，将选中的默认模型写回节点数据，并应用预设参数
  // 同时初始化 voice 为模型的第一个选项
  // 当切换模型时，如果当前 voice 不在新模型的 voices 列表中，则使用新模型的第一个 voice
  useEffect(() => {
    try {
      const updates: any = {};
      if (!data.model && modelId) {
        updates.model = modelId;
      }
      const presetParams = getPresetModelParams(modelId);
      if (displayVoices.length) {
        if (!data.voice) {
          const presetVoice = presetParams?.voice;
          updates.voice = presetVoice && displayVoices.includes(presetVoice) ? presetVoice : displayVoices[0];
        } else if (!displayVoices.includes(data.voice)) {
          updates.voice = displayVoices[0];
        }
      }
      const initUi = getInitialModelUi(modelId, (data as any)?.modelUi);
      if (initUi) {
        updates.modelUi = initUi;
      }
      if (Object.keys(updates).length > 0) {
        updateNodeData(id, updates);
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, modelId, data.model, data.voice, displayVoices]);

  const { isSubscribed, modelAccessMap, modelPassMap, userPlan, userInterval, userTags, planLevels, consumeModelPass } = useSubscription();
  const canUseAdvanced = isSubscribed;

  // ── Inline media (内连上传)：与图像/视频节点一致的参考音频上传 ──────────────
  const effectiveInlineMedia = useMemo<InlineMediaConfig>(
    () => ((model as any)?.inlineMedia as InlineMediaConfig | undefined) ?? { allowInline: false },
    [model],
  );
  const inlineAttachments = useMemo<InlineAttachment[]>(
    () => (Array.isArray((data as any)?.inlineAttachments) ? (data as any).inlineAttachments : []),
    [(data as any)?.inlineAttachments],
  );
  const handleInlineAttachmentsChange = useCallback(
    (next: InlineAttachment[]) => updateNodeData(id, { inlineAttachments: next }),
    [id, updateNodeData],
  );
  // 上传完成后重读最新值，避免用陈旧快照覆盖并发编辑（与图像/视频节点一致）。
  const getLatestInlineAttachments = useCallback((): InlineAttachment[] => {
    const d = (getNode(id) as any)?.data;
    return Array.isArray(d?.inlineAttachments) ? (d.inlineAttachments as InlineAttachment[]) : [];
  }, [getNode, id]);

  const selectIsSelected = useMemo(() => selectNodeSelectedById(id), [id]);
  const isTargetSelected = useStore(selectIsSelected);
  const incomingConnections = useNodeConnections({ id, handleType: 'target' });
  const connectedAudioItems = useMemo<InlineConnectedItem[]>(() => {
    // 仅在节点选中时计算，避免给未选中节点做无谓工作（与图像/视频节点一致）。
    if (!isTargetSelected) return [];
    const out: InlineConnectedItem[] = [];
    const seen = new Set<string>();
    for (const c of incomingConnections) {
      const sourceId = String((c as any)?.source || '');
      if (!sourceId || seen.has(sourceId)) continue;
      const n = getNode(sourceId) as any;
      if (!n || classifyNodeMediaType(n) !== 'audio') continue;
      seen.add(sourceId);
      const d = n.data ?? {};
      const rawUrl = d?.generated?.url || d?.generated?.urls?.[0] || d?.content?.url;
      // 上游已提交审核（Asset:// 引用）时用其预览 URL 播放
      const url = resolveAssetRefPreview(rawUrl, d?.assetPreviewUrl) ?? rawUrl;
      out.push({ id: sourceId, url, label: String(d?.instructions || d?.content?.name || t('Audio')), mediaType: 'audio' });
    }
    return out;
  }, [isTargetSelected, incomingConnections, getNode, t]);

  const focusConnectedNode = useCallback(
    (sourceId: string) => {
      try {
        const n = getNode(sourceId) as any;
        if (!n?.position) return;
        const w = Number(n?.measured?.width ?? n?.width ?? 0);
        const h = Number(n?.measured?.height ?? n?.height ?? 0);
        setCenter(n.position.x + w / 2, n.position.y + h / 2, { zoom: getZoom(), duration: 400 });
      } catch {}
    },
    [getNode, setCenter, getZoom],
  );

  const handleGenerate = useCallback(async () => {
    if (loading || !project?.id) return;
    setLoading(true);
    try {
      await createAudioHandleGenerate({ t, project, id, getNodes, getEdges, data, modelId, updateNodeData })();
      consumeModelPass(modelId);
      setTimeout(() => mutate(project?.id ? `credits:${project.id}` : 'credits'), 5000);
    } catch (error) {
      handleError(t('Error generating audio'), error);
    } finally {
      setLoading(false);
    }
  }, [loading, project?.id, id, getNodes, getEdges, data, modelId, updateNodeData, t, consumeModelPass]);

  const handleCancel = useCallback(() => {
    const gid = (getNode(id) as any)?.data?.processingGenerationId as string | undefined;
    updateNodeData(id, {
      status: undefined,
      processingGenerationId: undefined,
      runtimeEstimatedTime: undefined,
      error: undefined,
      updatedAt: new Date().toISOString(),
    });
    setLoading(false);
    toast(t('Generation cancelled'));
    if (gid) void cancelGeneration(gid);
  }, [id, getNode, updateNodeData, t]);

  useAutoGenerateHandler(
    id,
    useCallback(() => {
      try {
        void handleGenerate();
      } catch {}
    }, [handleGenerate]),
  );

  // Auto-cancel hook: lets the canvas-wide "Stop" button abort this node's
  // in-flight generation instead of letting it run to completion.
  useAutoCancelHandler(
    id,
    useCallback(() => {
      try {
        if (loading) handleCancel();
      } catch {}
    }, [loading, handleCancel]),
  );

  // Auto-run: wait until upstream inputs (text/image prompts) or instructions are available, then trigger once
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tryRun = () => {
      if (cancelled) return;
      if (!(data as any)?.autoRun || data.generated?.url || (data as any)?.generated?.urls?.length) return;

      const incomers = getIncomers({ id }, getNodes(), getEdges());
      const textPrompts = getTextFromTextNodes(incomers);
      const imagePrompts = getDescriptionsFromImageNodes(incomers);
      const hasInput = Boolean(textPrompts.length || imagePrompts.length || data.instructions);

      if (hasInput && !loading) {
        updateNodeData(id, { autoRun: false });
        setTimeout(() => {
          void handleGenerate();
        }, 0);
      } else {
        timer = setTimeout(tryRun, 700);
      }
    };

    timer = setTimeout(tryRun, 0);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const lyricsRef = useRef<HTMLTextAreaElement>(null);
  const adjustTextareaHeight = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.max(36, textareaRef.current.scrollHeight) + 'px';
    }
  }, []);
  const adjustLyricsHeight = useCallback(() => {
    if (lyricsRef.current) {
      lyricsRef.current.style.height = 'auto';
      lyricsRef.current.style.height = Math.max(36, Math.min(lyricsRef.current.scrollHeight, 120)) + 'px';
    }
  }, []);
  useEffect(() => { adjustTextareaHeight(); }, [data.instructions, adjustTextareaHeight]);
  useEffect(() => { adjustLyricsHeight(); }, [(data as any)?.modelUi?.musicLyrics, adjustLyricsHeight]);

  // Perf (Phase 9.5): stable identities for toolbar memo.
  const handleInstructionsChange = useCallback<ChangeEventHandler<HTMLTextAreaElement>>(
    (event) => updateNodeData(id, { instructions: event.target.value }),
    [id, updateNodeData],
  );
  const handleLyricsChange = useCallback<ChangeEventHandler<HTMLTextAreaElement>>(
    (event) =>
      updateNodeData(id, {
        modelUi: { ...((data as any)?.modelUi ?? {}), musicLyrics: event.target.value },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, updateNodeData, (data as any)?.modelUi],
  );

  const bgStyle = { backgroundColor: 'var(--node-bg)' } as const;
  const isToolbarBlocked = useMemo(() => Boolean((data as any)?.status === 'processing'), [(data as any)?.status]);

  const generatedUrls = (data as any)?.generated?.urls as string[] | undefined;
  const generatedUrl = (data as any)?.generated?.url as string | undefined;
  // 历史审核节点：generated.url 为 Asset:// 引用时用 assetPreviewUrl 播放/展示
  const assetPreviewUrl = (data as any)?.assetPreviewUrl as string | undefined;
  const mainUrl = useMemo(() => {
    // Asset:// 引用不可直接播放，用历史提审时保存的预览 URL 代替；
    // 预览缺失时与 image 节点一致，回退到 generated.urls[0]。
    const resolved = resolveAssetRefPreview(generatedUrl, assetPreviewUrl);
    if (resolved) return resolved;
    if (Array.isArray(generatedUrls) && generatedUrls.length > 0) return generatedUrls[0];
    return undefined;
  }, [generatedUrl, generatedUrls, assetPreviewUrl]);
  const multiUrls = useMemo(() => (Array.isArray(generatedUrls) ? generatedUrls.filter(Boolean) : []), [generatedUrls]);

  const modelUi = (data as any)?.modelUi ?? {};
  const uiOptions = useMemo<UiOption[]>(() => getUiOptionsForModel(modelId), [modelId]);

  // 音色选项可能多达数百个（如 MiniMax 332 个），且每项都要走 i18n 翻译，
  // 必须 memo 化，避免每次渲染都重建数组并触发数百次 t() 调用。
  const voiceOptions = useMemo<SimpleOption[]>(
    () =>
      displayVoices.map((v: string) => ({
        value: v,
        label: getSpeechVoiceLabel(model, v, t),
        keywords: [v, model?.voiceLabels?.[v]].filter(Boolean) as string[],
      })),
    [displayVoices, model, t],
  );

  const withMeta = filterModelsClient(speechModels, modelAccessMap, userPlan, userInterval, userTags, modelPassMap, planLevels);

  const modelUiJson = useMemo(() => {
    try { return JSON.stringify((data as any)?.modelUi ?? null); } catch { return null; }
  }, [(data as any)?.modelUi]);

  const estimatedUsdCost = useMemo(
    () => estimateSpeechUsdCost({ model, modelId, nodeData: data }),
    [model, modelId, data.instructions, (data as any)?.durationSeconds, modelUiJson],
  );
  const { credits: estimatedCredits, passRemaining, discountMultiplier, passCount, passDiscountMultiplier } = useEstimatedBilling(estimatedUsdCost, modelId);

  // Lyrics dialog
  const lyrics: string | undefined = useMemo(() => {
    try {
      const genLyrics = String(((data as any)?.generated as any)?.lyrics ?? '') || undefined;
      if (genLyrics) return genLyrics;
      const activeId = (data as any)?.activeHistoryId as string | undefined;
      const historyArr = (data as any)?.history as any[] | undefined;
      if (!activeId || !Array.isArray(historyArr)) return undefined;
      const item = historyArr.find((h) => String(h?.id) === String(activeId));
      const histLyrics = String((item as any)?.lyrics ?? '') || undefined;
      return histLyrics;
    } catch {
      return undefined;
    }
  }, [data]);
  const [isLyricsOpen, setIsLyricsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const isCollapsed = (() => {
    try {
      const n = getNode(id) as any;
      return Boolean(n?.data?.collapsed ?? (data as any)?.collapsed);
    } catch {
      return Boolean((data as any)?.collapsed);
    }
  })();

  // Persist durationSeconds once we have a generated URL (so collapsed summary can show stable duration).
  useEffect(() => {
    try {
      const genUrl = mainUrl;
      if (!genUrl) return;
      const existing = Number((data as any)?.durationSeconds);
      if (Number.isFinite(existing) && existing > 0) return;
      const el = new Audio();
      el.preload = 'metadata';
      el.src = genUrl;
      const onLoaded = () => {
        try {
          const dur = Number(el.duration);
          if (Number.isFinite(dur) && dur > 0) {
            updateNodeData(id, { durationSeconds: Math.round(dur * 100) / 100 });
          }
        } catch {}
        cleanup();
      };
      const onError = () => { cleanup(); };
      const cleanup = () => {
        try { el.removeEventListener('loadedmetadata', onLoaded); } catch {}
        try { el.removeEventListener('error', onError); } catch {}
      };
      el.addEventListener('loadedmetadata', onLoaded);
      el.addEventListener('error', onError);
      return () => cleanup();
    } catch {}
  }, [id, updateNodeData, mainUrl, (data as any)?.durationSeconds]);

  // 当当前生成有歌词但历史的当前项没有时，回填写入历史，避免切换后丢失
  useEffect(() => {
    try {
      const currentLyrics = (data as any)?.generated?.lyrics as string | undefined;
      const activeId = (data as any)?.activeHistoryId as string | undefined;
      const historyArr = (data as any)?.history as any[] | undefined;
      if (!currentLyrics || !activeId || !Array.isArray(historyArr) || historyArr.length === 0) return;
      const idx = historyArr.findIndex((h) => String(h?.id) === String(activeId));
      if (idx < 0) return;
      const item = historyArr[idx] || {};
      if (item.lyrics) return; // 已有，无需回填
      const nextHistory = historyArr.slice();
      nextHistory[idx] = { ...item, lyrics: currentLyrics };
      updateNodeData(id, { history: nextHistory });
    } catch {}
  }, [id, updateNodeData, data]);

  const handleSwitchToHistory = useCallback((historyItem: any) => {
    const nextUrl = historyItem?.url || (Array.isArray(historyItem?.urls) ? historyItem.urls[0] : undefined);
    if (!nextUrl) return;
    const nextUrls = Array.isArray(historyItem?.urls) ? historyItem.urls.filter(Boolean) : undefined;
    updateNodeData(id, {
      generated: nextUrls && nextUrls.length > 1
        ? { url: nextUrl, urls: nextUrls, type: historyItem?.type || 'audio/mpeg', lyrics: historyItem?.lyrics }
        : { url: nextUrl, type: historyItem?.type || 'audio/mpeg', lyrics: historyItem?.lyrics },
      activeHistoryId: historyItem?.id,
      status: 'completed',
      error: undefined,
    });
  }, [id, updateNodeData]);

  // 订阅语音生成 Realtime，完成/失败时本地即时回填（与图像同款逻辑）
  useAudioRealtime({
    projectId: project?.id,
    nodeId: id,
    data,
    modelId,
    updateNodeData,
    getNode,
    t,
  });

  useAudioPolling({
    projectId: project?.id,
    nodeId: id,
    data,
    modelId,
    updateNodeData,
    getNode,
    t,
  });

  const toolbar: ComponentProps<typeof NodeLayout>['toolbar'] = [
    {
      children: (() => {
        const COL_W: number | string | 'auto' = '100%';
        const hasVoiceSelector = displayVoices.length > 0;

        const modelControl = (
          <ModelItem
            key="model"
            id={id}
            value={modelId}
            options={withMeta}
            onChange={(value) => { rememberLastSelectedModelId('speech', value); updateNodeData(id, { model: value }); }}
            width={COL_W}
            disabled={isToolbarBlocked}
          />
        );

        const voiceControl = displayVoices.length ? (
          <SimpleSelectItem
            key="voice"
            id={`${id}-voice`}
            value={data.voice ?? displayVoices[0]}
            options={voiceOptions}
            onChange={(value) => updateNodeData(id, { voice: value })}
            width={COL_W}
            placeholder={t('Voice')}
            disabled={isToolbarBlocked}
          />
        ) : null;

        const onOptionChange = (key: string, val: unknown) =>
          updateNodeData(id, { modelUi: { ...(modelUi || {}), [key]: val } });

        const rowCls = "flex items-center gap-1.5 min-w-0 h-8";
        const labelCls2 = "flex items-center justify-center text-xs shrink-0 w-[60px]";

        const renderInlineOption = (opt: any) => {
          if (opt.type === 'slider') {
            const val = typeof modelUi?.[opt.key] === 'number' ? modelUi[opt.key] as number : opt.default;
            return (
              <div key={opt.key} className={rowCls}>
                <span className={labelCls2}>{t(opt.label)}</span>
                <Slider min={opt.min} max={opt.max} step={opt.step} value={[val]}
                  onValueChange={(vals: number[]) => onOptionChange(opt.key, Number(vals[0]))}
                  disabled={isToolbarBlocked} className="flex-1 min-w-[60px]" />
                <span className="text-xs tabular-nums w-[32px] text-right shrink-0">
                  {Number.isInteger(opt.step) ? val : val.toFixed(1)}
                </span>
              </div>
            );
          }
          if (opt.type === 'select') {
            const val = typeof modelUi?.[opt.key] === 'string' ? modelUi[opt.key] as string : opt.default;
            return (
              <div key={opt.key} className={rowCls}>
                <span className={labelCls2}>{t(opt.label)}</span>
                <div className="flex-1 min-w-0">
                  <SimpleSelectItem id={`${id}-${opt.key}`} value={val}
                    options={opt.options} onChange={(v) => onOptionChange(opt.key, v)}
                    disabled={isToolbarBlocked} width="100%" />
                </div>
              </div>
            );
          }
          if (opt.type === 'switch') {
            const val = typeof modelUi?.[opt.key] === 'boolean' ? modelUi[opt.key] as boolean : opt.default;
            return (
              <div key={opt.key} className={rowCls}>
                <span className={`${labelCls2} gap-1 w-auto max-w-[120px]`}>
                  <span className="truncate">{t(opt.label)}</span>
                  {opt.remark ? <OptionRemark text={t(opt.remark)} /> : null}
                </span>
                <button type="button" role="switch" aria-checked={val}
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${val ? 'bg-primary' : 'bg-muted'}`}
                  disabled={isToolbarBlocked}
                  onClick={() => onOptionChange(opt.key, !val)}>
                  <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-background shadow-sm transition-transform ${val ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            );
          }
          if (opt.type === 'input') {
            const val = typeof modelUi?.[opt.key] === 'string' ? modelUi[opt.key] as string : (opt.default ?? '');
            const randomAlpha = () => Array.from({ length: 8 }, () => 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)]).join('');
            return (
              <div key={opt.key} className={rowCls}>
                <span className={labelCls2}>{t(opt.label)}</span>
                <input
                  type="text"
                  className="flex-1 min-w-[60px] h-7 rounded-md border border-input bg-transparent px-2 text-[11px] placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring read-only:bg-muted/40 read-only:cursor-default read-only:focus-visible:ring-0"
                  value={val}
                  placeholder={t(opt.label)}
                  onChange={(e) => onOptionChange(opt.key, e.target.value)}
                  disabled={isToolbarBlocked}
                  readOnly={Boolean(opt.readonly)}
                />
                <button
                  type="button"
                  className="shrink-0 h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-muted transition-colors"
                  title={t('Copy')}
                  disabled={isToolbarBlocked || !val}
                  onClick={() => {
                    if (!val) return;
                    navigator.clipboard.writeText(val);
                    toast.success(t('Copied'));
                  }}
                >
                  <Copy size={13} className="text-muted-foreground" />
                </button>
                <button
                  type="button"
                  className="shrink-0 h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-muted transition-colors"
                  title={t('Random')}
                  disabled={isToolbarBlocked}
                  onClick={() => onOptionChange(opt.key, randomAlpha())}
                >
                  <Dice5 size={13} className="text-muted-foreground" />
                </button>
              </div>
            );
          }
          return null;
        };

        const inlineUiOptions = uiOptions.filter((o) => o.group === 'inline');
        const panelUiOptions = uiOptions.filter((o) => o.group !== 'inline');
        const inlineOptions = inlineUiOptions.map(renderInlineOption).filter(Boolean);

        const topControls = (
          <div
            className="grid gap-1.5 w-full items-center"
            style={{ gridTemplateColumns: hasVoiceSelector ? '1fr 1fr' : '1fr' }}
          >
            <div className="min-w-0">{modelControl}</div>
            {hasVoiceSelector ? <div className="min-w-0">{voiceControl}</div> : null}
          </div>
        );

        const showInlineMedia = Boolean(effectiveInlineMedia.allowInline);
        const middleControls = inlineOptions.length ? (
          <InlineOptionsRow>{inlineOptions}</InlineOptionsRow>
        ) : undefined;

        const actionControls: any[] = [];

        actionControls.push(
          <DownloadItem
            key="download"
            onDownload={() => {
              if (!mainUrl) return;
              download({ url: mainUrl, type: data.generated?.type || 'audio/mpeg' }, id, 'mp3');
            }}
            disabled={isToolbarBlocked || !mainUrl}
            downloadData={mainUrl ? { url: mainUrl, type: data.generated?.type || 'audio/mpeg' } : undefined}
          />
        );

        {
          const historyData = (data as any)?.history as any[] | undefined;
          actionControls.push(
            <HistoryItem
              key="history"
              items={historyData}
              activeId={(data as any)?.activeHistoryId}
              onSelect={(rec: any) => handleSwitchToHistory(rec)}
              disabled={isToolbarBlocked || !Array.isArray(historyData) || historyData.length === 0}
            />
          );
        }

        const advancedDisabled = isToolbarBlocked || panelUiOptions.length === 0;
        const advancedExpanded = advancedOpen && !advancedDisabled;
        if (panelUiOptions.length > 0) {
          actionControls.push(
            <AdvancedToggleButton
              key="advanced"
              expanded={advancedExpanded}
              disabled={advancedDisabled}
              onToggle={() => setAdvancedOpen((v) => !v)}
            />
          );
        }

        actionControls.push(
          <div key="generate-right" className="ml-auto flex items-center">
            <GenerateItem
              key="generate"
              loading={loading || (data as any)?.status === 'processing'}
              generatedUrl={mainUrl}
              onGenerate={handleGenerate}
              onCancel={(data as any)?.status === 'processing' ? handleCancel : undefined}
              disabled={loading || !project?.id || isToolbarBlocked}
              estimatedCredits={estimatedCredits}
              passRemaining={passRemaining}
              passCount={passCount}
              discountMultiplier={discountMultiplier}
              passDiscountMultiplier={passDiscountMultiplier}
            />
          </div>
        );

        const modelSupportsLyrics = Boolean(model?.voices?.includes('lyrics'));
        // 与 image/video 一致：默认显示文本输入框，模型通过 hideInstructions 字段隐藏。
        const showInstructions = !(model as any)?.hideInstructions;
        const hideLyricsVoices = (model as any)?.hideLyricsVoices as string[] | undefined;
        const showLyrics = modelSupportsLyrics && !(hideLyricsVoices ?? []).includes(data.voice ?? '');

        const promptLabel = t((model as any)?.promptLabel || 'Prompt');
        const lyricsLabel = t((model as any)?.lyricsLabel || 'Lyrics');
        const promptPlaceholder = (model as any)?.promptLabel ? t('Enter {{label}}...', { label: t((model as any).promptLabel) }) : t('Describe the song...');
        const lyricsPlaceholder = (model as any)?.lyricsPlaceholder
          ? t((model as any).lyricsPlaceholder)
          : (model as any)?.lyricsLabel
            ? t('Enter {{label}}...', { label: t((model as any).lyricsLabel) })
            : t('Enter lyrics here...');
        const instructionsContent = showInstructions ? (
          <div className="flex w-full flex-col">
            <InstructionRow
              icon={PROMPT_ROW_ICON}
              label={promptLabel}
              textareaRef={textareaRef}
              value={data.instructions ?? ''}
              onChange={handleInstructionsChange}
              onInput={adjustTextareaHeight}
              placeholder={promptPlaceholder}
            />
            {showLyrics ? (
              <InstructionRow
                icon={LYRICS_ROW_ICON}
                label={lyricsLabel}
                textareaRef={lyricsRef}
                value={modelUi?.musicLyrics ?? ''}
                onChange={handleLyricsChange}
                onInput={adjustLyricsHeight}
                placeholder={lyricsPlaceholder}
              />
            ) : null}
          </div>
        ) : undefined;

        const instructionsWithMedia = (showInlineMedia || instructionsContent) ? (
          <div className="flex w-full flex-col">
            {showInlineMedia ? (
              <div className="px-2 pt-2">
                <InlineMediaUploader
                  config={effectiveInlineMedia}
                  attachments={inlineAttachments}
                  onChange={handleInlineAttachmentsChange}
                  getLatestAttachments={getLatestInlineAttachments}
                  connectedItems={connectedAudioItems}
                  onFocusConnected={focusConnectedNode}
                  disabled={isToolbarBlocked}
                />
              </div>
            ) : null}
            {instructionsContent}
          </div>
        ) : undefined;

        return (
          <ThreeRowToolbar
            showInstructions={showInstructions || showInlineMedia}
            fixedWidthPx={720}
            instructions={instructionsWithMedia}
            topControls={topControls}
            middleControls={middleControls}
            bottomControls={actionControls}
            expandedPanel={
              advancedExpanded ? (
                <AdvancedInlinePanel
                  id={id}
                  uiOptions={panelUiOptions as any}
                  modelUi={modelUi as any}
                  canUseAdvanced={canUseAdvanced}
                  onChange={onOptionChange}
                />
              ) : undefined
            }
          />
        );
      })(),
    },
  ];

  return (
    <>
      <NodeLayout
        id={id}
        data={data}
        type={type}
        title={title}
        variant="modern"
        collapsedAspectRatio="16/9"
        collapsedSummary={<AudioCollapsedSummary data={data as any} />}
        titleBubble={!isCollapsed && lyrics ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <ModeChip as="button" className="justify-center gap-1" onClick={() => setIsLyricsOpen(true)} title={t('View lyrics')}>
                <AudioIcon size={10} />
                <span className="text-[10px]">{t('Lyrics')}</span>
              </ModeChip>
            </TooltipTrigger>
            <TooltipContent>{t('Click to view lyrics')}</TooltipContent>
          </Tooltip>
        ) : undefined}
        toolbar={toolbar}
        contextMenuItems={
          mainUrl
            ? [
                {
                  label: t('Share'),
                  onClick: () => setShareOpen(true),
                  icon: <Share2 size={12} />,
                },
              ]
            : undefined
        }
      >
      {/* 顶部工具栏：只在选中时挂载，避免每个节点都订阅 selection/nodes（与视频节点一致） */}
      {isTargetSelected ? (
        <AudioTopToolbar
          nodeId={id}
          audioUrl={mainUrl}
          isToolbarBlocked={isToolbarBlocked}
          onShare={() => setShareOpen(true)}
        />
      ) : null}
      {(data as any)?.status === 'processing' && (
        <ProcessingSurface
          aspectRatio={'1/1'}
          bgStyle={bgStyle}
          progress={progress}
          icon={<AudioIcon />}
          label={<div className="text-base font-medium text-muted-foreground text-center">{progress < 100 ? t('Generating audio… {{percent}}%', { percent: Number(progress).toFixed(2) }) : t('Generating audio…')}</div>}
          footer={<div className="text-sm text-muted-foreground/80 text-center">{Number(progress) >= 99 ? (
            <div className="opacity-80"><div>{t('eta_hint.line1')}</div><div>{t('eta_hint.line2')}</div><div>{t('eta_hint.line3')}</div></div>
          ) : (
            Number(progress) < 99 ? (
              <div className="opacity-80">{hasEstimate ? t('Estimated wait {{time}}', { time: remainingText }) : t('Calculating wait time…')}</div>
            ) : null
          )}</div>}
        />
      )}
      {(data as any)?.status === 'failed' && (
        <NodeSurface aspectRatio={'1/1'} bgStyle={bgStyle} showDashed={false}>
          <div className="relative flex h-full items-center justify-center p-6">
            <div className="text-center max-w-[90%]">
              <p className="inline-flex flex-wrap items-center justify-center gap-2 text-sm font-medium text-foreground">
                <span>{t('Audio generation failed')}</span>
                <span className="rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
                  {t('Credits refunded')}
                </span>
              </p>
              {(data as any)?.error ? (
                <p className="mt-1 text-xs text-muted-foreground break-words whitespace-pre-wrap">{String((data as any).error)}</p>
              ) : null}
            </div>
          </div>
        </NodeSurface>
      )}
      {(data as any)?.status !== 'processing' && (data as any)?.status !== 'failed' && !mainUrl && (
        <NodeSurface aspectRatio={'1/1'} bgStyle={bgStyle} showDashed={false}>
          <div className="relative flex h-full items-center justify-center p-6">
            <div
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
            >
              <div className="flex flex-col items-center gap-2 text-center">
                <p className="text-base font-medium text-foreground">{t('Ready to Create?')}</p>
                <p className="text-sm text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    <Zap size={12} className="opacity-80" />
                    {t('Click Generate to create audio')}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </NodeSurface>
      )}
      {(data as any)?.status !== 'processing' && (data as any)?.status !== 'failed' && mainUrl && (
        <NodeSurface aspectRatio={'1/1'} bgStyle={bgStyle} showDashed={false}>
          <div className="relative z-10 flex h-full flex-col items-center justify-center p-4 gap-2">
            <div className="w-full max-w-[560px]">
              <AudioPlayer src={mainUrl} className="w-full" />
            </div>
            {multiUrls.length > 1 && (
              <MultiAudioPicker
                urls={multiUrls}
                activeUrl={mainUrl}
                onChangeActive={(url) => updateNodeData(id, { generated: { ...(data as any)?.generated, url } })}
                disabled={isToolbarBlocked}
              />
            )}
          </div>
        </NodeSurface>
      )}
      {lyrics ? (
        <TextEditDialog
          open={isLyricsOpen}
          onOpenChange={setIsLyricsOpen}
          title={t('Lyrics')}
          footer={(
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" className="w-24" onClick={() => setIsLyricsOpen(false)}>
                {t('Close')}
              </Button>
            </div>
          )}
        >
          <EditorProvider
            immediatelyRender={false}
            content={textToTiptapJSON(lyrics)}
            placeholder={t('Lyrics')}
            editable={false}
            className={[
              'prose prose-sm dark:prose-invert size-full p-6',
              '[&_p:first-child]:mt-0',
              '[&_p:last-child]:mb-0',
            ].join(' ')}
          />
        </TextEditDialog>
      ) : null}
      </NodeLayout>

      <MusicShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        audioUrl={mainUrl || ''}
        defaultTitle={(() => {
          const ins = String((data as any)?.instructions ?? '').trim();
          if (ins) return ins.slice(0, 120);
          return title;
        })()}
        existingToken={(data as any)?.musicShareToken ?? null}
        existingCoverUrl={(data as any)?.musicShareCoverUrl ?? null}
        existingEnabled={(data as any)?.musicShareEnabled ?? null}
        onSaved={(token) => {
          try {
            updateNodeData(id, { musicShareToken: token, musicShareEnabled: true });
          } catch {}
        }}
        onStateChange={(st) => {
          try {
            const updates: any = {};
            if (typeof st.enabled === 'boolean') updates.musicShareEnabled = st.enabled;
            if (typeof st.coverUrl === 'string') updates.musicShareCoverUrl = st.coverUrl || null;
            if (Object.keys(updates).length) updateNodeData(id, updates);
          } catch {}
        }}
      />
    </>
  );
});
AudioTransform.displayName = 'AudioTransform';
